import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaTimes, FaSave, FaTrash, FaSpinner, FaUndo } from 'react-icons/fa';
import { IoIosColorPalette } from 'react-icons/io';
import { ImFontSize } from 'react-icons/im';
import { MdDriveFileRenameOutline } from 'react-icons/md';
import {
  normalizeHexColor,
  validateBrandKit,
  fetchUserBrandKitPresets,
  saveUserBrandKitPreset,
  setActiveBrandKitPreset
} from '../../services/brandKitService';

const BrandKitViewer = ({ userId, showAlertModal, showConfirmModal, onSave, onUnsavedChanges, isMobile = false }) => {
  const { t } = useTranslation();
  const [colors, setColors] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [showFontInput, setShowFontInput] = useState(false);
  const [newFontValue, setNewFontValue] = useState('');
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [editColorValue, setEditColorValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // 프리셋 관련 상태
  const [presets, setPresets] = useState({});
  const [activePresetId, setActivePresetId] = useState('preset1');
  const [showPresetModal, setShowPresetModal] = useState(false); // 모바일 프리셋 선택 모달

  // 원본 데이터 (변경사항 감지용)
  const [originalColors, setOriginalColors] = useState([]);
  const [originalFonts, setOriginalFonts] = useState([]);
  const [originalPresetName, setOriginalPresetName] = useState('');

  // 고급 색상 선택 상태
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeInputMethod, setActiveInputMethod] = useState('palette');
  const [hexInput, setHexInput] = useState('');
  const [rgbInput, setRgbInput] = useState({ r: 255, g: 0, b: 0 });
  const [previewColor, setPreviewColor] = useState('#FF0000');

  // 기본 색상 팔레트 (7x5 그리드)
  const defaultPalette = [
    ['#000000', '#424242', '#757575', '#9E9E9E', '#BDBDBD', '#E0E0E0', '#F5F5F5'],
    ['#FFCDD2', '#F8BBD9', '#E1BEE7', '#D1C4E9', '#C5CAE9', '#BBDEFB', '#B3E5FC'],
    ['#FF5252', '#FF4081', '#E040FB', '#7C4DFF', '#536DFE', '#448AFF', '#40C4FF'],
    ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4'],
    ['#D32F2F', '#C2185B', '#7B1FA2', '#512DA8', '#303F9F', '#1976D2', '#0288D1']
  ];

  // 변경사항 감지 함수
  const hasUnsavedChanges = useCallback(() => {
    const colorsChanged = JSON.stringify(colors) !== JSON.stringify(originalColors);
    const fontsChanged = JSON.stringify(fonts) !== JSON.stringify(originalFonts);
    const nameChanged = presetName !== originalPresetName;
    return colorsChanged || fontsChanged || nameChanged;
  }, [colors, fonts, presetName, originalColors, originalFonts, originalPresetName]);

  // 부모 컴포넌트에 변경사항 상태 전달
  useEffect(() => {
    if (onUnsavedChanges) {
      onUnsavedChanges(hasUnsavedChanges());
    }
  }, [hasUnsavedChanges, onUnsavedChanges]);

  // Firestore에서 모든 프리셋 로드
  const loadAllPresets = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError('');
    try {
      const { presets, activePresetId } = await fetchUserBrandKitPresets(userId);
      setPresets(presets);
      setActivePresetId(activePresetId);

      const activePreset = presets[activePresetId];
      if (activePreset) {
        const loadedColors = activePreset.colors || [];
        const loadedFonts = activePreset.fonts || [];
        const loadedName = activePreset.name || t('brandKit.preset', { num: activePresetId.slice(-1) });
        setColors(loadedColors);
        setFonts(loadedFonts);
        setPresetName(loadedName);
        // 원본 데이터 저장
        setOriginalColors(loadedColors);
        setOriginalFonts(loadedFonts);
        setOriginalPresetName(loadedName);
      }
    } catch (err) {
      setError(t('brandKit.loadError'));
      console.error('브랜드키트 로드 오류:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    loadAllPresets();
  }, [loadAllPresets]);

  // 현재 프리셋 저장 (통합 함수)
  // options: { showSuccessAlert: boolean, setAsActive: boolean }
  const saveCurrentPreset = useCallback(async (options = {}) => {
    const { showSuccessAlert = false, setAsActive = false } = options;

    try {
      const presetData = {
        name: presetName || t('brandKit.preset', { num: activePresetId.slice(-1) }),
        colors,
        fonts
      };

      const validation = validateBrandKit(presetData);
      if (!validation.isValid) {
        showAlertModal(t('brandKit.saveError') + ':\n' + validation.errors.join('\n'), 'error');
        return false;
      }

      await saveUserBrandKitPreset(userId, activePresetId, presetData);

      // 활성 프리셋으로 설정 (저장 버튼 클릭 시)
      if (setAsActive) {
        await setActiveBrandKitPreset(userId, activePresetId);
      }

      // 저장 후 로컬 presets 상태도 업데이트 (프리셋 전환 시 최신 데이터 사용)
      setPresets(prev => ({
        ...prev,
        [activePresetId]: {
          ...prev[activePresetId],
          name: presetName,
          colors: [...colors],
          fonts: [...fonts]
        }
      }));

      // 원본 데이터도 업데이트 (변경사항 감지 리셋)
      setOriginalColors([...colors]);
      setOriginalFonts([...fonts]);
      setOriginalPresetName(presetName);

      // 저장 후 onSave 콜백 호출 (칼라/폰트 검사 화면에서 최신 데이터 사용)
      if (onSave) {
        onSave({ colors, fonts });
      }

      // 성공 알림 표시 (저장 버튼 클릭 시)
      if (showSuccessAlert) {
        showAlertModal(t('brandKit.saveSuccess', { name: presetName }), 'success');
      }

      return true;
    } catch (err) {
      console.error('프리셋 저장 오류:', err);
      return false;
    }
  }, [presetName, activePresetId, colors, fonts, showAlertModal, userId, onSave, t]);

  // 특정 프리셋으로 전환 (저장 확인 포함)
  const switchToPreset = async (presetId) => {
    // 같은 프리셋이면 무시
    if (presetId === activePresetId) return;

    // 변경사항이 있는 경우 저장 확인
    if (hasUnsavedChanges()) {
      const confirmed = await showConfirmModal(
        t('brandKit.unsavedChanges')
      );

      if (confirmed) {
        // 저장 후 전환
        const saved = await saveCurrentPreset();
        if (!saved) {
          return;
        }
      }
      // 저장하지 않아도 전환 진행
    }

    // 프리셋 전환 실행
    try {
      await setActiveBrandKitPreset(userId, presetId);
      setActivePresetId(presetId);

      const selectedPreset = presets[presetId];
      if (selectedPreset) {
        const newColors = selectedPreset.colors || [];
        const newFonts = selectedPreset.fonts || [];
        const newName = selectedPreset.name || t('brandKit.preset', { num: presetId.slice(-1) });
        setColors(newColors);
        setFonts(newFonts);
        setPresetName(newName);
        setOriginalColors(newColors);
        setOriginalFonts(newFonts);
        setOriginalPresetName(newName);
      }

      if (onSave) {
        onSave({ colors: selectedPreset?.colors || [], fonts: selectedPreset?.fonts || [] });
      }
    } catch (err) {
      setError(t('brandKit.switchError'));
      console.error('프리셋 전환 오류:', err);
    }
  };

  // 외부에서 호출 가능한 저장 확인 함수 (화면 이동 시 사용)
  const checkUnsavedChangesBeforeLeave = useCallback(async () => {
    if (!hasUnsavedChanges()) return true;

    const confirmed = await showConfirmModal(
      t('brandKit.unsavedChangesLeave')
    );

    if (confirmed) {
      const saved = await saveCurrentPreset();
      return saved;
    }
    return true; // 저장 안 함 선택 시에도 이동 허용
  }, [hasUnsavedChanges, showConfirmModal, saveCurrentPreset, t]);

  // checkUnsavedChangesBeforeLeave를 부모에게 노출
  useEffect(() => {
    if (window) {
      window.brandKitCheckUnsavedChanges = checkUnsavedChangesBeforeLeave;
    }
    return () => {
      if (window) {
        delete window.brandKitCheckUnsavedChanges;
      }
    };
  }, [checkUnsavedChangesBeforeLeave]);

  const processHexInput = (input) => {
    const normalized = normalizeHexColor(input);
    return {
      isValid: normalized !== null,
      normalized: normalized
    };
  };

  const rgbToHex = (r, g, b) => {
    const toHex = (n) => {
      const hex = Math.round(n).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  };

  const selectPaletteColor = (color) => {
    setPreviewColor(color);
  };

  useEffect(() => {
    if (activeInputMethod === 'rgb') {
      const hexColor = rgbToHex(rgbInput.r, rgbInput.g, rgbInput.b);
      setPreviewColor(hexColor);
    }
  }, [rgbInput, activeInputMethod]);

  useEffect(() => {
    if (activeInputMethod === 'hex' && hexInput) {
      const { isValid, normalized } = processHexInput(hexInput);
      if (isValid && normalized) {
        setPreviewColor(normalized);
      }
    }
  }, [hexInput, activeInputMethod]);

  const addColorFromPicker = () => {
    if (colors.length >= 25) {
      showAlertModal(t('brandKit.maxColors'), 'warning');
      return;
    }

    let colorToAdd = previewColor;

    if (activeInputMethod === 'rgb') {
      colorToAdd = rgbToHex(rgbInput.r, rgbInput.g, rgbInput.b);
    } else if (activeInputMethod === 'hex') {
      const { isValid, normalized } = processHexInput(hexInput);
      if (!isValid || !normalized) {
        showAlertModal(t('brandKit.hexError'), 'warning');
        return;
      }
      colorToAdd = normalized;
    }

    const currentColors = [...colors];
    if (currentColors.includes(colorToAdd)) {
      showAlertModal(t('brandKit.duplicateColor'), 'warning');
      return;
    }

    setColors([...currentColors, colorToAdd]);
    setShowColorPicker(false);
    setHexInput('');
    setRgbInput({ r: 255, g: 0, b: 0 });
    setPreviewColor('#FF0000');
  };

  const editColor = (index) => {
    setEditingColorIndex(index);
    setEditColorValue(colors[index]);
  };

  const saveEditColor = () => {
    const { isValid, normalized } = processHexInput(editColorValue);
    if (isValid && normalized) {
      const otherColors = colors.filter((_, index) => index !== editingColorIndex);
      if (otherColors.includes(normalized)) {
        showAlertModal(t('brandKit.duplicateColorEdit'), 'warning');
        return;
      }
      const newColors = [...colors];
      newColors[editingColorIndex] = normalized;
      setColors(newColors);
      setEditingColorIndex(null);
      setEditColorValue('');
    } else {
      showAlertModal(t('brandKit.hexError'), 'warning');
    }
  };

  const removeColor = (index) => {
    const newColors = colors.filter((_, i) => i !== index);
    setColors(newColors);
  };

  const addFont = () => {
    if (newFontValue.trim()) {
      if (fonts.length >= 25) {
        showAlertModal(t('brandKit.maxFonts'), 'warning');
        return;
      }
      setFonts([...fonts, newFontValue.trim()]);
      setNewFontValue('');
      setShowFontInput(false);
    }
  };

  const removeFont = (index) => {
    setFonts(fonts.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const saved = await saveCurrentPreset({ showSuccessAlert: true, setAsActive: true });
      if (!saved) {
        // validation 실패 시 saveCurrentPreset에서 이미 알림 표시됨
        return;
      }
    } catch (err) {
      setError(t('brandKit.saveFailure') + err.message);
      console.error('브랜드키트 저장 오류:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      padding: '24px',
      background: '#f5f7fa',
      minHeight: '100vh',
      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            {t('brandKit.settingsTitle')}
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '8px 0 0 0'
          }}>
            {t('brandKit.settingsDesc')}
          </p>
        </div>

        {/* 프리셋 선택 */}
        {!loading && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* 모바일: 현재 프리셋만 표시하고 클릭시 모달 */}
            {isMobile ? (
              <button
                onClick={() => setShowPresetModal(true)}
                style={{
                  padding: '10px 16px',
                  marginTop: '54px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #1A62EA 0%, #3b82f6 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(26, 98, 234, 0.3)'
                }}
              >
                {presetName || t('brandKit.preset', { num: activePresetId.slice(-1) })}
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>
            ) : (
              /* PC: 기존 탭 형태 */
              <div style={{
                display: 'flex',
                gap: '8px',
                padding: '4px',
                background: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}>
                {['preset1', 'preset2', 'preset3'].map(presetId => {
                  const presetNumber = presetId.slice(-1);
                  const isActive = activePresetId === presetId;

                  return (
                    <button
                      key={presetId}
                      onClick={() => switchToPreset(presetId)}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        background: isActive
                          ? 'linear-gradient(135deg, #1A62EA 0%, #3b82f6 100%)'
                          : 'transparent',
                        color: isActive ? 'white' : '#6b7280',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: isActive ? '0 4px 12px rgba(26, 98, 234, 0.3)' : 'none'
                      }}
                    >
                      {t('brandKit.preset', { num: presetNumber })}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 로딩 상태 */}
      {loading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px',
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}>
          <FaSpinner style={{
            animation: 'spin 1s linear infinite',
            fontSize: '32px',
            color: '#1A62EA',
            marginBottom: '16px'
          }} />
          <div style={{ color: '#6b7280', fontSize: '16px' }}>{t('brandKit.loading')}</div>
        </div>
      )}

      {/* 에러 표시 */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* 메인 컨텐츠 */}
      {!loading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {/* 프리셋 이름 */}
          <div style={{
            background: '#ffffff',
            borderRadius: isMobile ? '12px' : '16px',
            padding: isMobile ? '12px 16px' : '20px 24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e5e7eb'
          }}>
            {/* 헤더: 아이콘 + 프리셋명 텍스트 + 초기화 버튼 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MdDriveFileRenameOutline style={{ fontSize: isMobile ? '24px' : '28px', color: '#1A62EA' }} />
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0
                }}>
                  {t('brandKit.presetName')}
                </h2>
              </div>
              {/* 초기화 버튼 */}
              <button
                onClick={() => {
                  setColors([]);
                  setFonts([]);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: isMobile ? '8px 12px' : '10px 16px',
                  background: '#fee2e2',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#fecaca';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#fee2e2';
                }}
              >
                <FaUndo style={{ fontSize: isMobile ? '10px' : '12px' }} />
                {t('brandKit.reset')}
              </button>
            </div>
            {/* 프리셋 이름 입력 */}
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder={t('brandKit.presetNamePlaceholder')}
              style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                color: '#1f2937',
                fontSize: isMobile ? '14px' : '18px',
                fontWeight: '600',
                padding: isMobile ? '10px 14px' : '12px 16px',
                width: '100%',
                maxWidth: isMobile ? '100%' : '400px',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#1A62EA'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          {/* 색상 섹션 */}
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <IoIosColorPalette style={{ fontSize: '28px', color: '#1A62EA' }} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                {t('brandKit.brandColors')}
              </h2>
              <span style={{
                fontSize: '13px',
                color: '#9ca3af',
                marginLeft: '8px'
              }}>
                ({colors.length}/25)
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
              gap: '16px'
            }}>
              {/* 기존 색상들 */}
              {colors.map((color, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  {editingColorIndex === index ? (
                    <div>
                      <input
                        type="text"
                        value={editColorValue}
                        onChange={(e) => setEditColorValue(e.target.value)}
                        placeholder="#FF0000"
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '12px',
                          marginBottom: '8px',
                          boxSizing: 'border-box'
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && saveEditColor()}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button
                          onClick={saveEditColor}
                          style={{
                            background: '#10b981',
                            border: 'none',
                            borderRadius: '6px',
                            color: 'white',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {t('brandKit.save')}
                        </button>
                        <button
                          onClick={() => setEditingColorIndex(null)}
                          style={{
                            background: '#6b7280',
                            border: 'none',
                            borderRadius: '6px',
                            color: 'white',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div
                        style={{
                          width: '70px',
                          height: '70px',
                          backgroundColor: color,
                          borderRadius: '12px',
                          margin: '0 auto 8px',
                          cursor: 'pointer',
                          border: '2px solid #e5e7eb',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}
                        onClick={() => editColor(index)}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.05)';
                          e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }}
                        title={t('brandKit.clickToEdit')}
                      />
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        marginBottom: '6px',
                        fontWeight: '500'
                      }}>
                        {color}
                      </div>
                      <button
                        onClick={() => removeColor(index)}
                        style={{
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#dc2626',
                          padding: '4px 10px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#fecaca';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#fee2e2';
                        }}
                      >
                        <FaTrash style={{ fontSize: '10px' }} />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* 색상 추가 버튼 */}
              {colors.length < 25 && (
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => setShowColorPicker(true)}
                    style={{
                      width: '70px',
                      height: '70px',
                      border: '2px dashed #d1d5db',
                      borderRadius: '12px',
                      background: '#f9fafb',
                      color: '#9ca3af',
                      fontSize: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#1A62EA';
                      e.target.style.color = '#1A62EA';
                      e.target.style.background = '#eff6ff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.color = '#9ca3af';
                      e.target.style.background = '#f9fafb';
                    }}
                  >
                    <FaPlus />
                  </button>
                  <div style={{
                    fontSize: '11px',
                    color: '#9ca3af',
                    marginTop: '8px'
                  }}>
                    {t('brandKit.addColor')}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 폰트 섹션 */}
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <ImFontSize style={{ fontSize: '24px', color: '#1A62EA' }} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                {t('brandKit.brandFonts')}
              </h2>
              <span style={{
                fontSize: '13px',
                color: '#9ca3af',
                marginLeft: '8px'
              }}>
                ({fonts.length}/25)
              </span>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {fonts.map((font, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1A62EA';
                  e.currentTarget.style.background = '#f0f7ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = '#f9fafb';
                }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontFamily: font,
                      fontSize: '18px',
                      color: '#1f2937',
                      marginBottom: '4px'
                    }}>
                      {font}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af'
                    }}>
                      {t('brandKit.fontPreview')}
                    </div>
                  </div>
                  <button
                    onClick={() => removeFont(index)}
                    style={{
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#dc2626',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#fecaca'}
                    onMouseLeave={(e) => e.target.style.background = '#fee2e2'}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}

              {/* 폰트 추가 */}
              {showFontInput ? (
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  border: '2px solid #1A62EA'
                }}>
                  <input
                    type="text"
                    value={newFontValue}
                    onChange={(e) => setNewFontValue(e.target.value)}
                    placeholder={t('brandKit.fontPlaceholder')}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && addFont()}
                    autoFocus
                  />
                  <button
                    onClick={addFont}
                    style={{
                      background: '#10b981',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      padding: '12px 20px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {t('brandKit.add')}
                  </button>
                  <button
                    onClick={() => {
                      setShowFontInput(false);
                      setNewFontValue('');
                    }}
                    style={{
                      background: '#6b7280',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      padding: '12px 20px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              ) : (
                fonts.length < 25 && (
                  <button
                    onClick={() => setShowFontInput(true)}
                    style={{
                      width: '100%',
                      padding: '20px',
                      border: '2px dashed #d1d5db',
                      borderRadius: '12px',
                      backgroundColor: '#f9fafb',
                      color: '#9ca3af',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#1A62EA';
                      e.target.style.color = '#1A62EA';
                      e.target.style.background = '#eff6ff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.color = '#9ca3af';
                      e.target.style.background = '#f9fafb';
                    }}
                  >
                    <FaPlus /> {t('brandKit.addFont')}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* 모바일 프리셋 선택 모달 */}
      {showPresetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowPresetModal(false)}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            width: '85%',
            maxWidth: '320px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              {t('brandKit.selectPreset')}
            </h3>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {['preset1', 'preset2', 'preset3'].map(presetId => {
                const isActive = activePresetId === presetId;
                const presetDisplayName = presets[presetId]?.name || t('brandKit.preset', { num: presetId.slice(-1) });

                return (
                  <button
                    key={presetId}
                    onClick={() => {
                      switchToPreset(presetId);
                      setShowPresetModal(false);
                    }}
                    style={{
                      padding: '14px 16px',
                      border: isActive ? '2px solid #1A62EA' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      background: isActive ? '#f0f7ff' : '#ffffff',
                      color: isActive ? '#1A62EA' : '#374151',
                      fontSize: '15px',
                      fontWeight: isActive ? '600' : '500',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {presetDisplayName}
                    {isActive && (
                      <span style={{
                        float: 'right',
                        color: '#1A62EA',
                        fontWeight: '700'
                      }}>✓</span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setShowPresetModal(false)}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '12px',
                border: 'none',
                borderRadius: '10px',
                background: '#f3f4f6',
                color: '#6b7280',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* 색상 선택 모달 */}
      {showColorPicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '32px',
            width: '90%',
            maxWidth: '550px',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
          }}>
            {/* 모달 헤더 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '22px',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                {t('brandKit.selectColor')}
              </h3>
              <button
                onClick={() => setShowColorPicker(false)}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: '#6b7280',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* 입력 방법 탭 */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '24px',
              padding: '4px',
              background: '#f3f4f6',
              borderRadius: '12px'
            }}>
              {[
                { key: 'palette', label: t('brandKit.palette') },
                { key: 'hex', label: t('brandKit.hexCode') },
                { key: 'rgb', label: 'RGB' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveInputMethod(tab.key)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    borderRadius: '8px',
                    background: activeInputMethod === tab.key
                      ? '#ffffff'
                      : 'transparent',
                    color: activeInputMethod === tab.key ? '#1f2937' : '#6b7280',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: activeInputMethod === tab.key ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 색상 미리보기 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px',
              padding: '16px',
              background: '#f9fafb',
              borderRadius: '12px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: previewColor,
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} />
              <div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  {previewColor}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {t('brandKit.selectedColor')}
                </div>
              </div>
            </div>

            {/* 입력 방법별 컨텐츠 */}
            <div style={{ marginBottom: '24px', minHeight: '250px' }}>
              {/* 팔레트 방식 */}
              {activeInputMethod === 'palette' && (
                <div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '8px',
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '12px'
                  }}>
                    {defaultPalette.flat().map((color, index) => (
                      <div
                        key={index}
                        onClick={() => selectPaletteColor(color)}
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          backgroundColor: color,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: previewColor === color
                            ? '3px solid #1A62EA'
                            : '2px solid #e5e7eb',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Hex 입력 방식 */}
              {activeInputMethod === 'hex' && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  padding: '24px',
                  background: '#f9fafb',
                  borderRadius: '12px'
                }}>
                  <input
                    type="text"
                    value={hexInput}
                    onChange={(e) => setHexInput(e.target.value)}
                    placeholder={t('brandKit.hexPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      fontSize: '18px',
                      fontWeight: '500',
                      textAlign: 'center',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    textAlign: 'center'
                  }}>
                    {t('brandKit.hexHelp')}
                  </div>
                </div>
              )}

              {/* RGB 슬라이더 방식 */}
              {activeInputMethod === 'rgb' && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  padding: '24px',
                  background: '#f9fafb',
                  borderRadius: '12px'
                }}>
                  {['r', 'g', 'b'].map((channel, index) => (
                    <div key={channel} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <span style={{
                        width: '30px',
                        fontSize: '16px',
                        fontWeight: '700',
                        color: ['#ef4444', '#22c55e', '#3b82f6'][index]
                      }}>
                        {channel.toUpperCase()}
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="255"
                        value={rgbInput[channel]}
                        onChange={(e) => setRgbInput(prev => ({
                          ...prev,
                          [channel]: parseInt(e.target.value)
                        }))}
                        style={{
                          flex: 1,
                          height: '8px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      />
                      <input
                        type="number"
                        min="0"
                        max="255"
                        value={rgbInput[channel]}
                        onChange={(e) => {
                          const value = Math.max(0, Math.min(255, parseInt(e.target.value) || 0));
                          setRgbInput(prev => ({ ...prev, [channel]: value }));
                        }}
                        style={{
                          width: '70px',
                          padding: '8px 12px',
                          fontSize: '14px',
                          textAlign: 'center',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db'
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 모달 하단 버튼 */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowColorPicker(false)}
                style={{
                  padding: '12px 24px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={addColorFromPicker}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #1A62EA 0%, #3b82f6 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(26, 98, 234, 0.3)'
                }}
              >
                {t('brandKit.addColor')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 우측 하단 고정 버튼들 */}
      {!loading && (
        <div style={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          display: 'flex',
          gap: '12px',
          zIndex: 100
        }}>
          {/* 복구 버튼 (수정사항이 있을 때만 표시) */}
          {hasUnsavedChanges() && (
            <button
              onClick={() => {
                setColors([...originalColors]);
                setFonts([...originalFonts]);
                setPresetName(originalPresetName);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                background: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                color: '#374151',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              <FaUndo />
              {t('brandKit.restore')}
            </button>
          )}
          {/* 저장 버튼 */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px',
              background: saving
                ? '#9ca3af'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
              transition: 'all 0.2s ease'
            }}
          >
            {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSave />}
            {saving ? t('brandKit.saving') : t('brandKit.save')}
          </button>
        </div>
      )}

      {/* 스핀 애니메이션 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `
      }} />
    </div>
  );
};

export default BrandKitViewer;
